import { useQuery } from "@tanstack/react-query";
import { ClipboardCheck, Layers, PackageCheck, Truck, Wrench } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { trpcClient } from "@/lib/trpc";
import { formatNumber } from "@/lib/utils";

interface Props {
  productionOrder: any;
  /** Dòng sản phẩm của lệnh (tab 1): qtyOrder, qtyDelivered */
  sourceItems: any[];
  onNavigateTab: (tab: string) => void;
}

type Tone = "ok" | "warn" | "idle";

const TONE_CLASS: Record<Tone, string> = {
  ok: "border-emerald-200 bg-emerald-50/50",
  warn: "border-amber-200 bg-amber-50/50",
  idle: "border-slate-200 bg-slate-50/50",
};
const TONE_TEXT: Record<Tone, string> = {
  ok: "text-emerald-700",
  warn: "text-amber-700",
  idle: "text-slate-500",
};

/**
 * Đầu trang lệnh sản xuất: 4 ô sẵn sàng triển khai (Vật tư · Khuôn & kẽm · KCS & đóng gói · Giao hàng).
 * Chỉ số liệu của lệnh đang xem; bấm ô mở tab tương ứng.
 */
export function POReadinessPanel({ productionOrder: lsx, sourceItems, onNavigateTab }: Props) {
  // Dùng chung cache với bảng Nhật ký KCS & Đóng gói (tab 5)
  const { data: qcData } = useQuery({
    queryKey: ["qc-final", "by-po", lsx.id],
    enabled: !!lsx.id,
    queryFn: async () =>
      (trpcClient as any).qcFinal.list.query({ productionOrderId: lsx.id, page: 1, limit: 200, sortBy: "checkTime", sortOrder: "desc" }),
  });
  const qcRows: any[] = qcData?.items || [];
  const qcPassed = qcRows.reduce((a, r) => a + (Number(r.qtyPassed) || 0), 0);

  const plans: any[] = lsx.materialPlans || [];
  const isPoMaterialReady = lsx.materialStatus === "READY";
  // "Đã cấp" chỉ tính dòng đã xuất kho thật cho lệnh (isFullyAllocated từ lượt cấp thực); đủ vật tư nhưng chưa xuất là "chờ cấp" (FB-CDA00F)
  const plansIssued = plans.filter((p) => p.isFullyAllocated).length;

  const toolings: any[] = lsx.toolings || [];
  const isPlate = (t: any) => t.toolingType === "PLATE" || t.toolingType === "ZINC";
  const molds = toolings.filter((t) => !isPlate(t));
  const plates = toolings.filter(isPlate);
  const qty = (rows: any[]) => rows.reduce((a, t) => a + Math.max(1, Number(t.quantity) || 1), 0);
  const toolingReady = toolings.filter((t) => t.readinessStatus === "READY").length;

  const ordered = sourceItems.reduce((a, s) => a + (Number(s.qtyOrder) || 0), 0);
  const delivered = sourceItems.reduce((a, s) => a + (Number(s.qtyDelivered) || 0), 0);
  const missing = Math.max(0, ordered - delivered);

  const tiles: { key: string; tab: string; label: string; icon: any; value: string; sub: string; tone: Tone }[] = [
    {
      key: "materials",
      tab: "materials",
      label: "Vật tư",
      icon: Layers,
      value: plans.length ? `${plansIssued}/${plans.length} dòng` : "Chưa lập",
      sub: plans.length
        ? plansIssued === plans.length
          ? "Đã cấp đủ"
          : isPoMaterialReady
            ? "Đủ vật tư, chờ cấp"
            : `Còn ${plans.length - plansIssued} dòng chờ cấp`
        : "Chưa có kế hoạch vật tư",
      tone: !plans.length ? "idle" : plansIssued === plans.length ? "ok" : "warn",
    },
    {
      key: "toolings",
      tab: "toolings",
      label: "Khuôn & kẽm",
      icon: Wrench,
      value: toolings.length ? `${qty(molds)} khuôn · ${qty(plates)} kẽm` : "Chưa lập",
      sub: toolings.length ? `${toolingReady}/${toolings.length} dòng sẵn sàng` : "Chưa có dòng khuôn/kẽm",
      tone: !toolings.length ? "idle" : toolingReady === toolings.length ? "ok" : "warn",
    },
    {
      key: "qc",
      tab: "finishing",
      label: "KCS & đóng gói",
      icon: PackageCheck,
      value: qcRows.length ? `${qcRows.length} phiếu` : "Chưa có phiếu",
      sub: qcRows.length ? `Đạt ${formatNumber(qcPassed)}` : "Ghi tại trang KCS & Đóng gói",
      tone: qcRows.length ? "ok" : "idle",
    },
    {
      key: "delivery",
      tab: "products",
      label: "Giao hàng",
      icon: Truck,
      value: `${formatNumber(delivered)}/${formatNumber(ordered)}`,
      sub: ordered === 0 ? "Chưa có số lượng đặt" : missing > 0 ? `Còn thiếu ${formatNumber(missing)}` : "Đã giao đủ",
      tone: ordered === 0 ? "idle" : missing > 0 ? (delivered > 0 ? "warn" : "idle") : "ok",
    },
  ];

  return (
    <Card className="md:col-span-4 shadow-sm border-slate-200 flex flex-col" data-testid="po-readiness-panel">
      <CardHeader className="py-2.5 px-4 bg-slate-50/60 border-b">
        <CardTitle className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
          <ClipboardCheck className="h-4 w-4 text-indigo-600" /> Sẵn sàng triển khai
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 grid grid-cols-2 gap-2 flex-1">
        {tiles.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              type="button"
              data-testid={`po-readiness-${t.key}`}
              onClick={() => onNavigateTab(t.tab)}
              className={`p-2.5 rounded border text-left flex flex-col justify-between hover:ring-2 hover:ring-indigo-200 transition ${TONE_CLASS[t.tone]}`}
            >
              <span className="text-[10px] uppercase font-semibold text-slate-500 flex items-center gap-1">
                <Icon className="h-3 w-3" /> {t.label}
              </span>
              <span className="mt-1 text-xs font-bold text-slate-800 tabular-nums">{t.value}</span>
              <span className={`text-[10px] ${TONE_TEXT[t.tone]}`}>{t.sub}</span>
            </button>
          );
        })}
      </CardContent>
    </Card>
  );
}
